import React, { useCallback, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { UploadCloud, Image as ImageIcon, AlertCircle, Sparkles, ShieldCheck, Zap } from 'lucide-react';

interface ImageUploaderProps {
  onImageUpload: (file: File) => void;
}

export function ImageUploader({ onImageUpload }: ImageUploaderProps) {
  const [error, setError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    setError(null);
    
    if (rejectedFiles.length > 0) {
      const error = rejectedFiles[0].errors[0];
      if (error.code === 'file-too-large') {
        setError('Image size must be less than 10MB');
      } else if (error.code === 'file-invalid-type') {
        setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      } else {
        setError('Error uploading file. Please try again.');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      onImageUpload(acceptedFiles[0]);
    }
  }, [onImageUpload]);

  const { getRootProps, getInputProps, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
      'image/webp': []
    },
    multiple: false,
    maxSize: 10485760, // 10MB
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false)
  });

  return (
    <div className="space-y-6 max-w-3xl mx-auto py-2">
      {/* Dropzone Container */}
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-3xl p-8 sm:p-12 transition-all duration-300 ease-out cursor-pointer group overflow-hidden
          ${
            isDragActive
              ? 'border-indigo-500 bg-indigo-50/80 dark:bg-indigo-950/40 scale-[1.01] shadow-2xl ring-4 ring-indigo-500/20'
              : 'border-slate-300 dark:border-slate-800 hover:border-indigo-400 dark:hover:border-indigo-500/70 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl shadow-lg hover:shadow-2xl'
          }
          ${isDragReject ? 'border-red-500 bg-red-50 dark:bg-red-950/30' : ''}
        `}
      >
        <input {...getInputProps()} />

        {/* Ambient Glowing Aura Background Effect */}
        <div className="absolute -top-24 -left-24 w-64 h-64 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-full blur-3xl pointer-events-none group-hover:scale-150 transition-transform duration-700" />
        <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-purple-500/10 dark:bg-purple-500/20 rounded-full blur-3xl pointer-events-none group-hover:scale-150 transition-transform duration-700" />
        
        <div className="relative z-10 flex flex-col items-center space-y-5 text-center">
          {/* Animated Floating Icon Circle */}
          <div
            className={`p-5 rounded-2xl transition-all duration-300 ${
              isDragActive
                ? 'bg-indigo-600 text-white scale-110 shadow-xl shadow-indigo-500/40 animate-bounce'
                : 'bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500 text-white shadow-lg shadow-indigo-500/20 group-hover:scale-110 group-hover:rotate-3'
            }`}
          >
            {isDragActive ? (
              <UploadCloud className="w-10 h-10" />
            ) : (
              <ImageIcon className="w-10 h-10" />
            )}
          </div>

          {/* Headline & Description */}
          <div className="space-y-1">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {isDragActive ? 'Drop your image here' : 'Drop your image or Browse'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
              Drag & drop any photo or click anywhere to select from your device
            </p>
          </div>

          {/* Supported Format Pills */}
          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            {['JPEG', 'PNG', 'WebP', 'GIF'].map((fmt) => (
              <span
                key={fmt}
                className="px-2.5 py-1 rounded-md text-[11px] font-bold font-mono bg-slate-100 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700/80 shadow-sm"
              >
                .{fmt}
              </span>
            ))}
            <span className="text-xs text-slate-400 ml-1">Up to 10MB</span>
          </div>

          {/* Select File Button */}
          <div className="pt-2">
            <button
              type="button"
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-lg shadow-indigo-600/30 group-hover:shadow-indigo-600/50 transition-all duration-300 flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Choose Photo
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-2xl text-red-700 dark:text-red-300 text-sm shadow-sm animate-shake">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Feature Badges Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
        <div className="p-3.5 rounded-2xl bg-white/60 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800/70 shadow-sm flex items-center gap-3">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">100% Client-Side</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Images stay on your device</p>
          </div>
        </div>

        <div className="p-3.5 rounded-2xl bg-white/60 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800/70 shadow-sm flex items-center gap-3">
          <div className="p-2 bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 rounded-xl">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">Instant Canvas Processing</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Zero loading delay</p>
          </div>
        </div>

        <div className="p-3.5 rounded-2xl bg-white/60 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800/70 shadow-sm flex items-center gap-3">
          <div className="p-2 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-xl">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">Social Presets</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Instagram, YouTube, Twitter & ID</p>
          </div>
        </div>
      </div>
    </div>
  );
}